/**
 * =============================================================================
 * Componente: ReferralCampaignFormModal
 * Versão: 1.0.0 (Fase 5 — Bloco 5C, item 5.7)
 *
 * Descrição:
 *   Modal de criação/edição de campanhas de indicação.
 *   - Vinculada obrigatoriamente a um programa existente.
 *   - Suporta tipo e valor de recompensa específicos da campanha.
 *   - Suporta janela temporal (start/end) e limites por afiliado.
 *   - Aceita regras de tier em formato JSON (campo avançado).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Link2,
  Copy,
  Check,
  Megaphone,
  Plus,
  Info,
  Gift,
  Globe,
  Image as ImageIcon,
  Briefcase,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  useReferralCampaignMutations,
  type AffiliateCampaign,
} from '@/hooks/useReferralCampaigns';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useContractTemplates } from '@/hooks/useContractTemplates';
import {
  ReferralCrmIntegrationSection,
  type ReferralCrmFormData,
} from './ReferralCrmIntegrationSection';
// v1.1.0 — Reutiliza o seletor universal de mídia (mesmo usado no modal de
// templates de campanha) para permitir escolher a imagem do hero direto da
// galeria do tenant, em vez de exigir colar uma URL manualmente.
import { MediaUrlInput } from '@/components/ui/media-url-input';
// v4.0.0 (2026-05-02) — Adoção do shell padronizado FormModalShell, que aplica
// o mesmo leiaute premium do modal de Tarefas (header com ícone em box,
// ScrollArea no corpo, footer fixo). A barra de abas (Geral · Recompensa ·
// Página · UTMs · Mídia & Regras · CRM) continua exibida via ModalTabsBar
// renderizada internamente pelo shell.
import { FormModalShell } from '@/components/shared/FormModalShell';
import type { ModalTabConfig } from '@/components/shared/ModalTabsBar';
import type { CampaignModalTab } from './modal/ReferralCampaignModalTabs';

// Configuração estática das abas do modal de campanha — mantida em paridade
// com ReferralCampaignModalTabs.tsx (id/label/ícone) para preservar o visual
// e a ordem já validados pelos usuários.
const CAMPAIGN_TABS: ModalTabConfig<CampaignModalTab>[] = [
  { id: 'general', label: 'Geral', icon: Info },
  { id: 'reward', label: 'Recompensa', icon: Gift },
  { id: 'landing', label: 'Página', icon: Globe },
  { id: 'utm', label: 'UTMs & Links', icon: Link2 },
  { id: 'media', label: 'Mídia & Regras', icon: ImageIcon },
  { id: 'crm', label: 'CRM', icon: Briefcase },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign?: AffiliateCampaign | null;
  defaultProgramId?: string | null;
}

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);

interface FormState {
  program_id: string;
  name: string;
  slug: string;
  description: string;
  reward_type: 'percentage' | 'fixed' | 'points' | 'tiered' | 'recurring';
  reward_value: string;
  starts_at: string;
  ends_at: string;
  max_per_affiliate: string;
  max_referrals: string;
  destination_url: string;
  default_utm_source: string;
  default_utm_medium: string;
  default_utm_campaign: string;
  default_utm_content: string;
  tiered_rules_json: string;
  is_active: boolean;
  // ----- Modelo Híbrido de Landing (v1) -----
  landing_mode: 'redirect' | 'landing' | 'builder';
  landing_title: string;
  landing_subtitle: string;
  landing_hero_image_url: string;
  landing_cta_label: string;
  landing_thank_you_title: string;
  landing_thank_you_message: string;
  landing_redirect_after_submit_seconds: string;
  landing_form_fields_json: string;
  // ----- Integração CRM (Etapa 4) -----
  crm_inherit_from_program: boolean;
  crm: ReferralCrmFormData;
  // ----- v2.0.0 (2026-05-01) Capa visual + Regras -----
  cover_media_url: string;
  cover_media_type: 'image' | 'video' | '';
  cover_storage_path: string;
  terms_template_id: string; // '' = nenhum
  // ----- v2.1.0 (2026-08-17) Campos do formulário de nova indicação -----
  /** Exibe o campo E-mail no modal "Nova Indicação" do painel do afiliado. */
  referral_form_show_email: boolean;
  /** Exibe o campo Observação no modal "Nova Indicação" do painel do afiliado. */
  referral_form_show_message: boolean;
}

// Formulário público padrão de indicação:
//  - Nome (obrigatório) → WhatsApp (obrigatório) → E-mail (opcional) → Observação (opcional).
//  - O campo "message" do tipo textarea é mapeado pela RPC submit_referral_lead
//    para affiliate_referrals.lead_message (mesmo campo usado na criação manual).
const DEFAULT_FORM_FIELDS_JSON = JSON.stringify(
  [
    { key: 'name', label: 'Nome completo', type: 'text', required: true, order: 1 },
    { key: 'phone', label: 'WhatsApp', type: 'phone', required: true, order: 2 },
    { key: 'email', label: 'E-mail', type: 'email', required: false, order: 3 },
    {
      key: 'message',
      label: 'Observação',
      type: 'textarea',
      required: false,
      order: 4,
      placeholder: 'Conte algo sobre a indicação (opcional)',
    },
  ],
  null,
  2,
);

const emptyState: FormState = {
  program_id: '',
  name: '',
  slug: '',
  description: '',
  reward_type: 'percentage',
  reward_value: '10',
  starts_at: '',
  ends_at: '',
  max_per_affiliate: '',
  max_referrals: '',
  destination_url: '',
  default_utm_source: '',
  default_utm_medium: 'affiliate',
  default_utm_campaign: '',
  default_utm_content: '',
  tiered_rules_json: '',
  is_active: true,
  landing_mode: 'redirect',
  landing_title: '',
  landing_subtitle: '',
  landing_hero_image_url: '',
  landing_cta_label: 'Quero saber mais',
  landing_thank_you_title: 'Obrigado!',
  landing_thank_you_message: 'Em breve entraremos em contato.',
  landing_redirect_after_submit_seconds: '',
  landing_form_fields_json: DEFAULT_FORM_FIELDS_JSON,
  crm_inherit_from_program: true,
  crm: {
    auto_create_contact: true,
    auto_create_deal: false,
    crm_workspace_id: null,
    crm_pipeline_id: null,
    crm_phase_id: null,
    crm_source: null,
    crm_default_seller_id: null,
    crm_distribution_queue_id: null,
    crm_default_tags: [],
    crm_deal_title_template: null,
    crm_assignment_mode: null, // v1.3.0
  },
  cover_media_url: '',
  cover_media_type: '',
  cover_storage_path: '',
  terms_template_id: '',
  // v2.1.0 — por padrão o formulário do afiliado mostra todos os campos
  referral_form_show_email: true,
  referral_form_show_message: true,
};

/** Converte ISO → input[type=datetime-local]. */
const toLocalDt = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Monta a URL final que o visitante verá após o redirect 302,
 * com as UTMs da campanha anexadas (preservando querystring existente).
 * Retorna null se a URL base for inválida ou vazia.
 */
function buildFinalUrlPreview(form: FormState): string | null {
  const raw = form.destination_url.trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const params = url.searchParams;
  // Defaults coerentes com a RPC get_or_create_campaign_link
  const utmSource = form.default_utm_source.trim() || 'indicacao';
  const utmMedium = form.default_utm_medium.trim() || 'affiliate';
  const utmCampaign = form.default_utm_campaign.trim() || form.slug.trim() || 'campanha';
  const utmContent = form.default_utm_content.trim();
  // Set sobrescreve se já existir o mesmo nome (comportamento esperado)
  params.set('utm_source', utmSource);
  params.set('utm_medium', utmMedium);
  params.set('utm_campaign', utmCampaign);
  if (utmContent) params.set('utm_content', utmContent);
  return url.toString();
}

/** Monta um exemplo de link curto com short_code fictício. */
function buildShortLinkPreview(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin.replace(/\/$/, '')}/r/AB12CD34`;
}

export function ReferralCampaignFormModal({
  open,
  onOpenChange,
  campaign,
  defaultProgramId,
}: Props) {
  const { toast } = useToast();
  const { data: programs = [] } = useReferralPrograms();
  const { createMutation, updateMutation } = useReferralCampaignMutations();
  const [form, setForm] = useState<FormState>(emptyState);
  const [copiedKey, setCopiedKey] = useState<'short' | 'final' | null>(null);
  // v3.0.0 — Aba ativa do modal (resetada ao abrir)
  const [activeTab, setActiveTab] = useState<CampaignModalTab>('general');

  // Previews recalculados em tempo real conforme o usuário edita
  const finalUrlPreview = useMemo(() => buildFinalUrlPreview(form), [form]);
  const shortLinkPreview = useMemo(() => buildShortLinkPreview(), []);

  const handleCopy = async (value: string, key: 'short' | 'final') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast({ title: 'Link copiado!' });
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
    } catch {
      toast({ title: 'Não foi possível copiar', variant: 'destructive' });
    }
  };

  useEffect(() => {
    if (!open) return;
    // Sempre reinicia na primeira aba ao reabrir o modal
    setActiveTab('general');
    if (campaign) {
      setForm({
        program_id: campaign.program_id,
        name: campaign.name ?? '',
        slug: campaign.slug ?? '',
        description: campaign.description ?? '',
        reward_type:
          (campaign.reward_type as FormState['reward_type']) ?? 'percentage',
        reward_value: String(campaign.reward_value ?? 0),
        starts_at: toLocalDt(campaign.starts_at),
        ends_at: toLocalDt(campaign.ends_at),
        max_per_affiliate: campaign.max_per_affiliate?.toString() ?? '',
        max_referrals: campaign.max_referrals?.toString() ?? '',
        destination_url: (campaign as any).destination_url ?? '',
        default_utm_source: (campaign as any).default_utm_source ?? '',
        default_utm_medium: (campaign as any).default_utm_medium ?? 'affiliate',
        default_utm_campaign: (campaign as any).default_utm_campaign ?? '',
        default_utm_content: (campaign as any).default_utm_content ?? '',
        tiered_rules_json: campaign.tiered_rules
          ? JSON.stringify(campaign.tiered_rules, null, 2)
          : '',
        is_active: !!campaign.is_active,
        landing_mode: ((campaign as any).landing_mode ?? 'redirect') as FormState['landing_mode'],
        landing_title: (campaign as any).landing_title ?? '',
        landing_subtitle: (campaign as any).landing_subtitle ?? '',
        landing_hero_image_url: (campaign as any).landing_hero_image_url ?? '',
        landing_cta_label: (campaign as any).landing_cta_label ?? 'Quero saber mais',
        landing_thank_you_title: (campaign as any).landing_thank_you_title ?? 'Obrigado!',
        landing_thank_you_message:
          (campaign as any).landing_thank_you_message ?? 'Em breve entraremos em contato.',
        landing_redirect_after_submit_seconds:
          (campaign as any).landing_redirect_after_submit_seconds?.toString() ?? '',
        landing_form_fields_json: (campaign as any).landing_form_fields
          ? JSON.stringify((campaign as any).landing_form_fields, null, 2)
          : DEFAULT_FORM_FIELDS_JSON,
        crm_inherit_from_program: (campaign as any).crm_inherit_from_program ?? true,
        crm: {
          auto_create_contact: true, // sempre true (forçado por segurança)
          auto_create_deal: (campaign as any).auto_create_deal ?? false,
          crm_workspace_id: (campaign as any).crm_workspace_id ?? null,
          crm_pipeline_id: (campaign as any).crm_pipeline_id ?? null,
          crm_phase_id: (campaign as any).crm_phase_id ?? null,
          crm_source: (campaign as any).crm_source ?? null,
          crm_default_seller_id: (campaign as any).crm_default_seller_id ?? null,
          crm_distribution_queue_id: (campaign as any).crm_distribution_queue_id ?? null,
          crm_default_tags: (campaign as any).crm_default_tags ?? [],
          crm_deal_title_template: (campaign as any).crm_deal_title_template ?? null,
          // v1.3.0 — modo persistido (fixed_seller | queue | null = herda do programa)
          crm_assignment_mode:
            ((campaign as any).crm_assignment_mode ?? null) as
              | 'fixed_seller'
              | 'queue'
              | null,
        },
        cover_media_url: (campaign as any).cover_media_url ?? '',
        cover_media_type: ((campaign as any).cover_media_type ?? '') as 'image' | 'video' | '',
        cover_storage_path: (campaign as any).cover_storage_path ?? '',
        terms_template_id: (campaign as any).terms_template_id ?? '',
        // v2.1.0 — flags do formulário de indicação (default true para legados)
        referral_form_show_email:
          (campaign as any).referral_form_show_email ?? true,
        referral_form_show_message:
          (campaign as any).referral_form_show_message ?? true,
      });
    } else {
      setForm({ ...emptyState, program_id: defaultProgramId ?? '' });
    }
  }, [open, campaign, defaultProgramId]);

  const isEdit = !!campaign?.id;
  const saving = createMutation.isPending || updateMutation.isPending;

  const handleNameChange = (value: string) => {
    setForm((p) => ({
      ...p,
      name: value,
      slug: !isEdit && (!p.slug || p.slug === slugify(p.name)) ? slugify(value) : p.slug,
    }));
  };

  const handleSubmit = async () => {
    if (!form.program_id) {
      toast({ title: 'Selecione um programa.', variant: 'destructive' });
      return;
    }
    if (!form.name.trim() || !form.slug.trim()) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Informe nome e slug.',
        variant: 'destructive',
      });
      return;
    }
    // URL de destino: obrigatória só no modo redirect; opcional nos demais.
    const destUrl = form.destination_url.trim();
    if (form.landing_mode === 'redirect') {
      if (!destUrl || !/^https?:\/\//i.test(destUrl)) {
        toast({
          title: 'URL de destino obrigatória',
          description:
            'No modo Redirect a URL é obrigatória (http:// ou https://).',
          variant: 'destructive',
        });
        return;
      }
    } else if (destUrl && !/^https?:\/\//i.test(destUrl)) {
      toast({
        title: 'URL de destino inválida',
        description: 'Se preenchida, deve iniciar com http:// ou https://',
        variant: 'destructive',
      });
      return;
    }

    // Validação dos campos do formulário (modo landing)
    let landingFields: any[] | null = null;
    if (form.landing_mode === 'landing') {
      try {
        landingFields = JSON.parse(form.landing_form_fields_json);
        if (!Array.isArray(landingFields) || landingFields.length === 0) {
          throw new Error('vazio');
        }
        const hasContact = landingFields.some(
          (f: any) => f?.type === 'email' || f?.type === 'phone',
        );
        if (!hasContact) {
          toast({
            title: 'Formulário incompleto',
            description:
              'Inclua ao menos um campo do tipo e-mail ou WhatsApp para conseguir contatar o lead.',
            variant: 'destructive',
          });
          return;
        }
      } catch {
        toast({
          title: 'Configuração de formulário inválida',
          description: 'Verifique o JSON dos campos da landing.',
          variant: 'destructive',
        });
        return;
      }
    }

    // Validação opcional do JSON de tiers
    let tiered: any = null;
    if (form.tiered_rules_json.trim()) {
      try {
        tiered = JSON.parse(form.tiered_rules_json);
      } catch {
        toast({
          title: 'JSON de tiers inválido',
          description: 'Verifique a sintaxe das regras avançadas.',
          variant: 'destructive',
        });
        return;
      }
    }

    const payload = {
      program_id: form.program_id,
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim() || null,
      reward_type: form.reward_type,
      reward_value: Number(form.reward_value) || 0,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      max_per_affiliate: form.max_per_affiliate
        ? Number(form.max_per_affiliate)
        : null,
      max_referrals: form.max_referrals ? Number(form.max_referrals) : null,
      destination_url: destUrl || null,
      default_utm_source: form.default_utm_source.trim() || null,
      default_utm_medium: form.default_utm_medium.trim() || 'affiliate',
      default_utm_campaign: form.default_utm_campaign.trim() || null,
      default_utm_content: form.default_utm_content.trim() || null,
      tiered_rules: tiered,
      is_active: form.is_active,
      // ----- Modelo Híbrido de Landing -----
      landing_mode: form.landing_mode,
      landing_title: form.landing_title.trim() || null,
      landing_subtitle: form.landing_subtitle.trim() || null,
      landing_hero_image_url: form.landing_hero_image_url.trim() || null,
      landing_cta_label: form.landing_cta_label.trim() || 'Quero saber mais',
      landing_thank_you_title:
        form.landing_thank_you_title.trim() || 'Obrigado!',
      landing_thank_you_message:
        form.landing_thank_you_message.trim() ||
        'Em breve entraremos em contato.',
      landing_redirect_after_submit_seconds:
        form.landing_redirect_after_submit_seconds
          ? Number(form.landing_redirect_after_submit_seconds)
          : null,
      landing_form_fields:
        landingFields ?? JSON.parse(form.landing_form_fields_json || '[]'),
      // ----- Integração CRM (Etapa 4) -----
      crm_inherit_from_program: form.crm_inherit_from_program,
      auto_create_contact: true, // sempre true — evita indicações órfãs
      auto_create_deal: form.crm.auto_create_deal,
      crm_workspace_id: form.crm.crm_workspace_id,
      crm_pipeline_id: form.crm.crm_pipeline_id,
      crm_phase_id: form.crm.crm_phase_id,
      crm_source: form.crm.crm_source,
      crm_default_seller_id: form.crm.crm_default_seller_id,
      crm_distribution_queue_id: form.crm.crm_distribution_queue_id,
      crm_default_tags: form.crm.crm_default_tags,
      crm_deal_title_template: form.crm.crm_deal_title_template,
      crm_assignment_mode: form.crm.crm_assignment_mode, // v1.3.0
      // v2.0.0 (2026-05-01) — Capa + Regras
      cover_media_url: form.cover_media_url.trim() || null,
      cover_media_type: form.cover_media_type === '' ? null : form.cover_media_type,
      cover_storage_path: form.cover_storage_path.trim() || null,
      terms_template_id: form.terms_template_id || null,
      // v2.1.0 — campos exibidos no formulário de nova indicação do afiliado
      referral_form_show_email: form.referral_form_show_email,
      referral_form_show_message: form.referral_form_show_message,
    };

    try {
      if (isEdit && campaign) {
        await updateMutation.mutateAsync({ id: campaign.id, patch: payload });
        toast({ title: 'Campanha atualizada com sucesso.' });
      } else {
        await createMutation.mutateAsync(payload as any);
        toast({ title: 'Campanha criada com sucesso.' });
      }
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar campanha',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModalShell<CampaignModalTab>
      open={open}
      onOpenChange={onOpenChange}
      icon={Megaphone}
      title={isEdit ? 'Editar campanha' : 'Nova campanha'}
      description="Configure as regras de recompensa específicas desta campanha."
      tabs={CAMPAIGN_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onSubmit={handleSubmit}
      isSubmitting={saving}
      submitLabel={isEdit ? 'Salvar alterações' : 'Criar campanha'}
      submitIcon={isEdit ? Check : Plus}
    >
      {/* O FormModalShell já aplica padding (p-6) e space-y entre seções.
          Wrapper interno preserva o gap-4 original entre os blocos de campos
          dentro de cada aba (mantendo a aparência idêntica ao layout anterior). */}
      <div className="grid gap-4">

          {/* =====================================================================
              ABA: Geral — identidade básica e status
             ===================================================================== */}
          {activeTab === 'general' && (
            <>
              <div className="grid gap-2">
                <Label>Programa *</Label>
                <Select
                  value={form.program_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, program_id: v }))}
                  disabled={isEdit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um programa" />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map((prg) => (
                      <SelectItem key={prg.id} value={prg.id}>
                        {prg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Ex.: Black Friday 2026"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={form.slug}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, slug: slugify(e.target.value) }))
                    }
                    placeholder="black-friday-2026"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="desc">Descrição</Label>
                <Textarea
                  id="desc"
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Campanha ativa</Label>
                  <p className="text-xs text-muted-foreground">
                    Quando inativa, deixa de ser exibida para os afiliados.
                  </p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
                />
              </div>

              {/* ---------------------------------------------------------------
                  v2.1.0 — Formulário de indicação do painel do afiliado.
                  Permite reduzir a quantidade de campos exibidos ao afiliado
                  no modal "Nova Indicação". Nome e WhatsApp são sempre exibidos.
                 --------------------------------------------------------------- */}
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Formulário de indicação do afiliado
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Escolha quais campos aparecem no modal "Nova Indicação" do
                    painel do afiliado. Nome e WhatsApp são sempre exibidos.
                  </p>
                </div>

                <div className="flex items-center justify-between rounded-lg border bg-background p-3">
                  <Label className="font-normal">Exibir campo E-mail</Label>
                  <Switch
                    checked={form.referral_form_show_email}
                    onCheckedChange={(v) =>
                      setForm((p) => ({ ...p, referral_form_show_email: v }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border bg-background p-3">
                  <Label className="font-normal">Exibir campo Observação</Label>
                  <Switch
                    checked={form.referral_form_show_message}
                    onCheckedChange={(v) =>
                      setForm((p) => ({ ...p, referral_form_show_message: v }))
                    }
                  />
                </div>
              </div>
            </>
          )}


          {/* =====================================================================
              ABA: Recompensa — regras financeiras, janela temporal e limites
             ===================================================================== */}
          {activeTab === 'reward' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Tipo de recompensa</Label>
                  <Select
                    value={form.reward_type}
                    onValueChange={(v: FormState['reward_type']) =>
                      setForm((p) => ({ ...p, reward_type: v }))
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Comissão (%)</SelectItem>
                      <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                      {/* Opções ocultas até implementação/validação completa da feature:
                          - "points" (Pontos)
                          - "recurring" (Recorrente)
                          - "tiered" (Por tiers)
                          O type union em FormState foi mantido para compatibilidade
                          com registros legados eventualmente já gravados. */}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rval">
                    Valor *{' '}
                    {form.reward_type === 'fixed' && (
                      <span className="text-xs text-muted-foreground">(R$)</span>
                    )}
                    {form.reward_type === 'percentage' && (
                      <span className="text-xs text-muted-foreground">(%)</span>
                    )}
                  </Label>
                  {form.reward_type === 'fixed' ? (
                    /* Máscara monetária BRL: usuário digita apenas dígitos,
                       interpretados como centavos. Estado guarda valor numérico
                       em reais (ex.: "150.00") para envio à RPC. */
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        R$
                      </span>
                      <Input
                        id="rval"
                        type="text"
                        inputMode="numeric"
                        className="pl-9"
                        value={
                          form.reward_value
                            ? (Number(form.reward_value) || 0).toLocaleString(
                                'pt-BR',
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )
                            : ''
                        }
                        onChange={(e) => {
                          // Mantém somente dígitos e converte de centavos para reais
                          const onlyDigits = e.target.value.replace(/\D/g, '');
                          const numeric = onlyDigits
                            ? (Number(onlyDigits) / 100).toFixed(2)
                            : '';
                          setForm((p) => ({ ...p, reward_value: numeric }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  ) : form.reward_type === 'percentage' ? (
                    /* Máscara percentual: aceita decimais, sufixo "%" exibido
                       à direita. Estado mantém string decimal (ex.: "5" ou "7.5"). */
                    <div className="relative">
                      <Input
                        id="rval"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="pr-9"
                        value={form.reward_value}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, reward_value: e.target.value }))
                        }
                        placeholder="0.00"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        %
                      </span>
                    </div>
                  ) : (
                    /* Fallback para tipos legados (points/recurring/tiered) que
                       eventualmente apareçam em campanhas antigas. */
                    <Input
                      id="rval"
                      type="number"
                      step="0.01"
                      value={form.reward_value}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, reward_value: e.target.value }))
                      }
                    />
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="start">Início</Label>
                  <Input
                    id="start"
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, starts_at: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="end">Fim</Label>
                  <Input
                    id="end"
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, ends_at: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="mpa">Máx. por afiliado</Label>
                  <Input
                    id="mpa"
                    type="number"
                    value={form.max_per_affiliate}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, max_per_affiliate: e.target.value }))
                    }
                    placeholder="Sem limite"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="mr">Máx. total de indicações</Label>
                  <Input
                    id="mr"
                    type="number"
                    value={form.max_referrals}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, max_referrals: e.target.value }))
                    }
                    placeholder="Sem limite"
                  />
                </div>
              </div>

              {/* Bloco "Regras de tier (JSON avançado)" ocultado até a feature de
                  recompensa por tiers ser reimplementada e validada. O estado
                  `tiered_rules_json` permanece no FormState para preservar a
                  hidratação de campanhas legadas sem causar perda de dados. */}
            </>
          )}

          {/* =====================================================================
              ABA: Página de destino — modo redirect/landing/builder + URL + landing
             ===================================================================== */}
          {activeTab === 'landing' && (
            <>
              <div className="grid gap-2">
                <Label>Tipo de página de destino *</Label>
                <Select
                  value={form.landing_mode}
                  onValueChange={(v: FormState['landing_mode']) =>
                    setForm((p) => ({ ...p, landing_mode: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="redirect">
                      Redirect — vai direto para uma URL externa
                    </SelectItem>
                    <SelectItem value="landing">
                      Landing com formulário — captura o lead na nossa página
                    </SelectItem>
                    <SelectItem value="builder" disabled>
                      Builder visual (em breve)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.landing_mode === 'redirect'
                    ? 'O visitante é redirecionado para a URL externa cadastrada abaixo.'
                    : form.landing_mode === 'landing'
                    ? 'O visitante vê uma landing hospedada com formulário e o lead é criado automaticamente vinculado ao afiliado.'
                    : 'Em breve.'}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="dest">
                  URL de destino{' '}
                  {form.landing_mode === 'redirect' ? '*' : '(opcional)'}
                </Label>
                <Input
                  id="dest"
                  type="url"
                  value={form.destination_url}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, destination_url: e.target.value }))
                  }
                  placeholder="https://meusite.com/oferta"
                />
                <p className="text-xs text-muted-foreground">
                  {form.landing_mode === 'redirect'
                    ? 'Para onde todos os links desta campanha vão redirecionar.'
                    : 'Opcional. Se preenchida, o lead é encaminhado para esta URL após enviar o formulário.'}
                </p>
              </div>

              {/* Bloco condicional — Configurações da Landing (somente quando landing_mode === 'landing') */}
              {form.landing_mode === 'landing' && (
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                  <div className="text-sm font-semibold text-foreground">
                    Configurações da Landing Page
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="land_title">Título do hero</Label>
                      <Input
                        id="land_title"
                        value={form.landing_title}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, landing_title: e.target.value }))
                        }
                        placeholder="Ex.: Comece seu teste grátis"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="land_cta">Texto do botão (CTA)</Label>
                      <Input
                        id="land_cta"
                        value={form.landing_cta_label}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            landing_cta_label: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="land_sub">Subtítulo</Label>
                    <Textarea
                      id="land_sub"
                      rows={2}
                      value={form.landing_subtitle}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, landing_subtitle: e.target.value }))
                      }
                      placeholder="Texto curto explicando a oferta"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="land_hero">Imagem de fundo (hero)</Label>
                    <MediaUrlInput
                      value={form.landing_hero_image_url || null}
                      onChange={(url) =>
                        setForm((p) => ({
                          ...p,
                          landing_hero_image_url: url ?? '',
                        }))
                      }
                      placeholder="https://… (opcional) ou escolha da galeria"
                      allowedTypes={['image']}
                      showPreview
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="land_ty_t">Título de agradecimento</Label>
                      <Input
                        id="land_ty_t"
                        value={form.landing_thank_you_title}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            landing_thank_you_title: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="land_redir">
                        Redirecionar após X segundos
                      </Label>
                      <Input
                        id="land_redir"
                        type="number"
                        min={0}
                        value={form.landing_redirect_after_submit_seconds}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            landing_redirect_after_submit_seconds: e.target.value,
                          }))
                        }
                        placeholder="Ex.: 5 (deixe vazio para não redirecionar)"
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="land_ty_m">Mensagem de agradecimento</Label>
                    <Textarea
                      id="land_ty_m"
                      rows={2}
                      value={form.landing_thank_you_message}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          landing_thank_you_message: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="land_fields">Campos do formulário (JSON)</Label>
                    <Textarea
                      id="land_fields"
                      rows={6}
                      value={form.landing_form_fields_json}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          landing_form_fields_json: e.target.value,
                        }))
                      }
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      Cada campo: <code>{'{"key", "label", "type", "required", "order"}'}</code>.
                      Tipos suportados: <code>text</code>, <code>email</code>, <code>phone</code>,{' '}
                      <code>textarea</code>, <code>select</code>. É obrigatório ao menos
                      um campo do tipo <code>email</code> ou <code>phone</code>.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* =====================================================================
              ABA: UTMs & Links — parâmetros UTM + preview de links
             ===================================================================== */}
          {activeTab === 'utm' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="utm_source">UTM source</Label>
                  <Input
                    id="utm_source"
                    value={form.default_utm_source}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, default_utm_source: e.target.value }))
                    }
                    placeholder="ex.: indicacao"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="utm_medium">UTM medium</Label>
                  <Input
                    id="utm_medium"
                    value={form.default_utm_medium}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, default_utm_medium: e.target.value }))
                    }
                    placeholder="affiliate"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="utm_campaign">UTM campaign</Label>
                  <Input
                    id="utm_campaign"
                    value={form.default_utm_campaign}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, default_utm_campaign: e.target.value }))
                    }
                    placeholder="(usa o slug se vazio)"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="utm_content">UTM content</Label>
                  <Input
                    id="utm_content"
                    value={form.default_utm_content}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, default_utm_content: e.target.value }))
                    }
                    placeholder="opcional"
                  />
                </div>
              </div>

              {/* Preview de links da campanha */}
              <div className="rounded-md border border-dashed bg-muted/40 p-3 space-y-3">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Link2 className="h-3.5 w-3.5" />
                  Preview dos links desta campanha
                </div>

                {/* Link curto exemplo */}
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Link curto compartilhado pelo afiliado
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
                    <code className="flex-1 truncate text-xs font-mono text-foreground">
                      {shortLinkPreview}
                    </code>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 shrink-0"
                      onClick={() => handleCopy(shortLinkPreview, 'short')}
                      aria-label="Copiar link curto"
                    >
                      {copiedKey === 'short' ? (
                        <Check className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Cada afiliado recebe um <code className="font-mono">short_code</code> único — este é apenas um exemplo.
                  </p>
                </div>

                {/* URL final pós-redirect com UTMs */}
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    URL final que o visitante verá (após redirect)
                  </div>
                  {finalUrlPreview ? (
                    <>
                      <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
                        <code className="flex-1 break-all text-xs font-mono text-foreground">
                          {finalUrlPreview}
                        </code>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 shrink-0"
                          onClick={() => handleCopy(finalUrlPreview, 'final')}
                          aria-label="Copiar URL final"
                        >
                          {copiedKey === 'final' ? (
                            <Check className="h-3.5 w-3.5 text-primary" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        As UTMs são anexadas automaticamente ao gerar o link de cada afiliado.
                      </p>
                    </>
                  ) : (
                    <div className="rounded-md border border-dashed border-border px-2 py-2 text-[11px] text-muted-foreground italic">
                      Preencha uma URL de destino válida (http:// ou https://) para visualizar o preview.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* =====================================================================
              ABA: Mídia & Regras — capa visual + modelo de contrato (termos)
             ===================================================================== */}
          {activeTab === 'media' && (
            <>
              {/* Capa visual da campanha (imagem ou vídeo) */}
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="text-sm font-semibold text-foreground">
                  Capa da Campanha (opcional)
                </div>
                <p className="text-xs text-muted-foreground">
                  Aparece como destaque visual no card da campanha no portal do afiliado.
                  Escolha uma imagem ou um vídeo curto explicando como funciona.
                </p>

                <div className="grid gap-2">
                  <Label>Tipo</Label>
                  <Select
                    value={form.cover_media_type || 'image'}
                    onValueChange={(v: 'image' | 'video') =>
                      setForm((p) => ({ ...p, cover_media_type: v }))
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Imagem</SelectItem>
                      <SelectItem value="video">Vídeo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>{form.cover_media_type === 'video' ? 'URL do vídeo' : 'Imagem'}</Label>
                  {form.cover_media_type === 'video' ? (
                    <>
                      <MediaUrlInput
                        value={form.cover_media_url || null}
                        onChange={(url) =>
                          setForm((p) => ({
                            ...p,
                            cover_media_url: url ?? '',
                            cover_media_type: 'video',
                          }))
                        }
                        placeholder="https://… (mp4/webm)"
                        allowedTypes={['video']}
                      />
                      {form.cover_media_url && (
                        <video
                          src={form.cover_media_url}
                          controls
                          preload="metadata"
                          className="w-full max-h-48 rounded-md border"
                        />
                      )}
                    </>
                  ) : (
                    <MediaUrlInput
                      value={form.cover_media_url || null}
                      onChange={(url) =>
                        setForm((p) => ({
                          ...p,
                          cover_media_url: url ?? '',
                          cover_media_type: 'image',
                        }))
                      }
                      placeholder="https://… ou escolha da galeria"
                      allowedTypes={['image']}
                      showPreview
                    />
                  )}
                </div>
              </div>

              {/* Regras da Campanha (vínculo com modelo de contrato) */}
              <TermsTemplateSelector
                value={form.terms_template_id}
                onChange={(v) => setForm((p) => ({ ...p, terms_template_id: v }))}
              />
            </>
          )}

          {/* =====================================================================
              ABA: CRM — integração com pipeline (cria contato/deal automaticamente)
             ===================================================================== */}
          {activeTab === 'crm' && (
            <ReferralCrmIntegrationSection
              form={form.crm}
              onChange={(updates) =>
                setForm((p) => ({ ...p, crm: { ...p.crm, ...updates } }))
              }
              isCampaignContext
            />
          )}
      </div>
    </FormModalShell>
  );
}


// =============================================================================
// Subcomponente: seletor de modelo de contrato (Regras da Campanha)
// =============================================================================
function TermsTemplateSelector({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { templates = [], isLoading } = useContractTemplates();

  // Prioriza categorias relacionadas a indicação, mas mostra todas.
  const sorted = [...templates].sort((a: any, b: any) => {
    const isAffA = ['affiliate', 'referral', 'indicacao'].includes(
      (a.category ?? '').toLowerCase(),
    );
    const isAffB = ['affiliate', 'referral', 'indicacao'].includes(
      (b.category ?? '').toLowerCase(),
    );
    if (isAffA && !isAffB) return -1;
    if (!isAffA && isAffB) return 1;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <div className="text-sm font-semibold text-foreground">
        Regras da Campanha (opcional)
      </div>
      <p className="text-xs text-muted-foreground">
        Vincule um modelo de contrato com as regras de participação. O afiliado
        verá o conteúdo ao clicar em "Regras" no card da campanha. Reaproveita
        os modelos cadastrados no módulo de Contratos do CRM.
      </p>
      <div className="grid gap-2">
        <Label>Modelo de contrato</Label>
        <Select
          value={value || '__none__'}
          onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
          disabled={isLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sem regras" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Sem regras</SelectItem>
            {sorted.map((t: any) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {t.category ? ` · ${t.category}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">
          Não encontrou? Crie um novo modelo em{' '}
          <a
            href="/crm/modelos-contrato"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-foreground"
          >
            CRM › Modelos de Contrato
          </a>
          .
        </p>
      </div>
    </div>
  );
}
